/**
 * Core Solitaire (Klondike) Game Types
 *
 * Standard 52-card deck. 7 tableau columns, 4 foundation piles,
 * stock pile, waste pile. Single-player card game.
 */

// ============================================================================
// Card Types
// ============================================================================

/** Card suit */
export type Suit = 'hearts' | 'diamonds' | 'clubs' | 'spades';

/** Card color derived from suit */
export type CardColor = 'red' | 'black';

/** A single playing card */
export interface Card {
  /** Unique identifier for this card */
  id: string;
  /** Card suit */
  suit: Suit;
  /** Card rank: 1 (Ace) through 13 (King) */
  rank: number;
  /** Whether the card is face-up (visible) */
  faceUp: boolean;
}

// ============================================================================
// Game State
// ============================================================================

/** Source location for a card move */
export interface CardSource {
  type: 'stock' | 'waste' | 'foundation' | 'tableau';
  /** Pile index (0-3 for foundations, 0-6 for tableau) */
  index: number;
  /** Card index within the pile (for tableau sequences) */
  cardIndex: number;
}

/** A recorded move for undo support */
export interface SolitaireMove {
  /** Source of the moved card(s) */
  from: CardSource;
  /** Destination of the moved card(s) */
  to: CardSource;
  /** Number of cards moved */
  cardCount: number;
  /** Whether a card was flipped face-up after the move */
  flippedCard: boolean;
  /** Whether this was a stock-to-waste draw */
  wasDraw: boolean;
  /** Number of cards drawn (for draw-3 mode) */
  drawnCount: number;
  /** Whether this was a stock reset (waste back to stock) */
  wasStockReset: boolean;
  timestamp: number;
}

/** Full game state */
export interface SolitaireGameState {
  /** Stock pile (face-down draw pile) */
  stock: Card[];
  /** Waste pile (drawn cards) */
  waste: Card[];
  /** 4 foundation piles (one per suit, build Ace to King) */
  foundations: Card[][];
  /** 7 tableau columns */
  tableau: Card[][];
  /** Move history for undo */
  moveHistory: SolitaireMove[];
  /** Elapsed time in seconds */
  timer: number;
  /** Total moves made */
  moveCount: number;
  /** Current game status */
  gameStatus: 'idle' | 'playing' | 'completed' | 'paused';
  /** Draw mode: 1 or 3 cards at a time */
  drawMode: 1 | 3;
  /** Unique game identifier */
  gameId: string;
  /** Timestamp when the game started */
  startedAt: number;
}

// ============================================================================
// Game Settings
// ============================================================================

export interface SolitaireGameSettings {
  drawMode: 1 | 3;
}

// ============================================================================
// Statistics
// ============================================================================

export interface SolitaireDrawModeStats {
  gamesPlayed: number;
  gamesWon: number;
  bestTime: number | null;
  averageTime: number;
  winPercentage: number;
  fewestMoves: number | null;
}

export interface SolitaireStatistics {
  userId?: string;
  draw1: SolitaireDrawModeStats;
  draw3: SolitaireDrawModeStats;
  totalGames: number;
  totalPlayTime: number;
  currentStreak: number;
  bestStreak: number;
}

export interface SolitaireGameResult {
  gameId: string;
  drawMode: 1 | 3;
  completed: boolean;
  time: number;
  moveCount: number;
  completedAt: number;
}

// ============================================================================
// Component Props
// ============================================================================

export interface SolitaireGameProps {
  onExit?: () => void;
  initialRoomCode?: string;
}

export interface SolitaireGameModeSelectorProps {
  onStartGame: (settings: SolitaireGameSettings) => void;
  guestGamesRemaining?: number;
  isAuthenticated?: boolean;
}

export interface SolitaireBoardProps {
  gameState: SolitaireGameState;
  selectedCard: { source: CardSource; card: Card } | null;
  onCardClick: (source: CardSource, card: Card) => void;
  onEmptyClick: (source: CardSource) => void;
  onCardDoubleClick: (source: CardSource, card: Card) => void;
  onStockClick: () => void;
  disabled?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

export const SUITS: Suit[] = ['hearts', 'diamonds', 'clubs', 'spades'];

export const SUIT_SYMBOLS: Record<Suit, string> = {
  hearts: '♥',
  diamonds: '♦',
  clubs: '♣',
  spades: '♠',
};

export const RANK_LABELS: Record<number, string> = {
  1: 'A',
  2: '2',
  3: '3',
  4: '4',
  5: '5',
  6: '6',
  7: '7',
  8: '8',
  9: '9',
  10: '10',
  11: 'J',
  12: 'Q',
  13: 'K',
};

/** Get the color of a suit */
export function getSuitColor(suit: Suit): CardColor {
  return suit === 'hearts' || suit === 'diamonds' ? 'red' : 'black';
}

/** Get the color of a card */
export function getCardColor(card: Card): CardColor {
  return getSuitColor(card.suit);
}
